import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { AuthContextType, AuthProviderProps } from './AuthContext.types';
import { useAuth0 } from '@auth0/auth0-react';
import { axiosInstance, axiosReportingInstance } from 'src/api/axios/axiosInstance';
import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { signalR } from 'src/signalR';
import { isTokenExpired } from 'src/utils/helpers';
import { getRedirectUri } from 'src/components/AuthWrapper/Auth0Provider';
import { buildHostWithSlug } from 'src/utils/buildHostWithSlug';
import type { FanguardApiContractsUserUserDetailModel } from 'src/api/generated';

const VITE_API_PATH = import.meta.env.VITE_API_PATH ?? 'access.wicketsoft.com';
const VITE_LOCAL_API_PATH = import.meta.env.VITE_LOCAL_API_PATH ?? 'localhost:5555';
const VITE_LOCAL_API_PATH_REPORTING = import.meta.env.VITE_LOCAL_API_PATH_REPORTING ?? 'localhost:6666';
const IS_LOCAL = import.meta.env.VITE_IS_LOCAL_API === 'true';

const AuthContext = createContext<AuthContextType | null>(null);

interface LocalTokenPayload {
  sub?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  sys_admin?: boolean;
  sysadmin?: 'True' | 'False';
}

const currentUserFromLocalToken = (token: string): FanguardApiContractsUserUserDetailModel | null => {
  try {
    const decoded = JSON.parse(atob(token.split('.')[1])) as LocalTokenPayload;
    const nameParts = decoded.name?.trim().split(/\s+/) ?? [];
    const firstName = decoded.given_name ?? nameParts[0] ?? null;
    const lastName = decoded.family_name ?? (nameParts.length > 1 ? nameParts.slice(1).join(' ') : null);
    const isSysAdmin = decoded.sysadmin?.toLowerCase?.() === 'true' || decoded.sys_admin === true;

    return {
      username: decoded.sub ?? null,
      firstName: firstName ?? null,
      lastName: lastName ?? null,
      email: decoded.email ?? null,
      sysAdmin: isSysAdmin,
    };
  } catch {
    return null;
  }
};

const currentUserFromAuth0User = (user: {
  sub?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  email?: string;
}): FanguardApiContractsUserUserDetailModel => {
  return {
    username: user.sub ?? null,
    firstName: user.given_name ?? null,
    lastName: user.family_name ?? null,
    email: user.email ?? null,
    sysAdmin: false,
  };
};

const initSignalR = async (token: string) => {
  try {
    const baseUrl = `${axiosInstance.defaults.baseURL}/notification`;
    await signalR.ensureConnection(baseUrl, token);
  } catch (error) {
    console.error('Failed to connesct to SignalR:', error);
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, configAuthData }) => {
  const ldClient = useLDClient();
  const {
    user,
    isAuthenticated: auth0IsAuthenticated,
    logout: auth0Logout,
    getAccessTokenSilently,
    isLoading: auth0IsLoading,
    loginWithRedirect,
  } = useAuth0();

  const getTokenPermissions = (token: string) => {
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    const permissions = decodedToken.scopes ? JSON.parse(decodedToken.scopes)[0].permissions : decodedToken.permissions;
    return permissions;
  };

  const login = (token: string) => {
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    const username = decodedToken.sub;
    const client = decodedToken.scopes
      ? JSON.parse(decodedToken.scopes)[0].client
      : window.location.hostname.split('.')[0];
    const permissions = getTokenPermissions(token);
    initSignalR(token);
    ldClient?.identify({
      key: `${client}`,
      kind: 'client',
    });
    setAuthData({
      username,
      isAuthenticated: true,
      permissions,
    });
    if (configAuthData?.platform === 'Local') {
      setCurrentUser(currentUserFromLocalToken(token));
    }
  };

  const [currentUser, setCurrentUser] = useState<FanguardApiContractsUserUserDetailModel | null>(null);

  const [authData, setAuthData] = useState<Omit<AuthContextType, 'login' | 'logout' | 'currentUser'>>(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken && isTokenExpired(refreshToken)) {
      return { isAuthenticated: false, username: null, permissions: [] };
    }

    if (refreshToken && token && isTokenExpired(token)) {
      return { isAuthenticated: true, username: null, permissions: [] };
    }

    if (!token) {
      return { isAuthenticated: false, username: null, permissions: [] };
    }

    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    const username = decodedToken.sub as string;
    const client = decodedToken.scopes
      ? JSON.parse(decodedToken.scopes)[0].client
      : window.location.hostname.split('.')[0];
    initSignalR(token);
    ldClient?.identify({
      key: `${client}`,
      kind: 'client',
    });
    const permissions = getTokenPermissions(token);
    return {
      isAuthenticated: true,
      username,
      permissions,
    };
  });

  const addBearerAuth0Token = async (token: string) => {
    const slug = window.location.hostname.split('.')[0]; // cannot rely on client name, needs domain slug for auth0 clients
    const apiHost = buildHostWithSlug(slug, VITE_API_PATH);

    if (IS_LOCAL) {
      axiosInstance.defaults.baseURL = `https://${VITE_LOCAL_API_PATH}`;
      axiosReportingInstance.defaults.baseURL = `https://${VITE_LOCAL_API_PATH_REPORTING}`;
    } else {
      axiosInstance.defaults.baseURL = `${location.protocol}//${apiHost}/api`;
      axiosReportingInstance.defaults.baseURL = `${location.protocol}//${apiHost}/api-reporting`;
    }

    // // Clear existing interceptors to avoid conflicts
    axiosInstance.interceptors.request.clear();
    axiosInstance.interceptors.response.clear();
    axiosReportingInstance.interceptors.request.clear();
    axiosReportingInstance.interceptors.response.clear();

    const trimHeader = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

    const applyAuth0ClientHeader = (config: InternalAxiosRequestConfig) => {
      const explicit = trimHeader(config.headers.get('Client'));
      if (explicit) {
        config.headers.set('Client', explicit);
      } else {
        config.headers.set('Client', configAuthData?.client ?? '');
      }
    };

    axiosInstance.interceptors.request.use(
      (config) => {
        config.headers.set('Authorization', `Bearer-Auth0 ${token}`);
        applyAuth0ClientHeader(config);
        return config;
      },
      (error: AxiosError): Promise<AxiosError> => {
        return Promise.reject(error);
      },
    );
    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError): Promise<AxiosError> => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          const token = await getAuth0Token();
          if (token) {
            error.config?.headers.set('Authorization', `Bearer-Auth0 ${token}`);
          }
        }
        return Promise.reject(error);
      },
    );
    axiosReportingInstance.interceptors.request.use((config) => {
      config.headers.set('Authorization', `Bearer-Auth0 ${token}`);
      applyAuth0ClientHeader(config);
      return config;
    });
    axiosReportingInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError): Promise<AxiosError> => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          const token = await getAuth0Token();
          if (token) {
            error.config?.headers.set('Authorization', `Bearer-Auth0 ${token}`);
          }
        }
        return Promise.reject(error);
      },
    );
  };

  const getAuth0Token = async () => {
    try {
      const auth0Token = await getAccessTokenSilently();
      await addBearerAuth0Token(auth0Token);
      login(auth0Token);

      return auth0Token;
    } catch (error) {
      console.error(error);
    }
  };

  const logoutFromAuth0 = () => {
    if (auth0IsAuthenticated) {
      auth0Logout({
        logoutParams: {
          returnTo: getRedirectUri(),
        },
      });
    }
  };

  const logout = () => {
    logoutFromAuth0();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    signalR.stop();
    setCurrentUser(null);
    setAuthData({
      isAuthenticated: false,
      username: null,
      permissions: [],
    });
  };

  // Derive currentUser from Auth0 id_token profile (user object)
  useEffect(() => {
    if (configAuthData?.platform === 'Auth0' && auth0IsAuthenticated && user) {
      setCurrentUser(currentUserFromAuth0User(user));
    }
  }, [configAuthData?.platform, auth0IsAuthenticated, user]);

  // Derive currentUser from Local access_token
  useEffect(() => {
    if (configAuthData?.platform === 'Local' && authData.isAuthenticated) {
      const token = localStorage.getItem('token');
      if (token && !isTokenExpired(token)) {
        setCurrentUser(currentUserFromLocalToken(token));
      }
    }
  }, [configAuthData?.platform, authData.isAuthenticated]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken && token && isTokenExpired(token)) {
      login(token);
    }
  }, []);

  useEffect(() => {
    if (auth0IsAuthenticated) {
      getAuth0Token();
    }
  }, [user?.sub, auth0IsAuthenticated]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const error = searchParams.get('error');
    const code = searchParams.get('code');
    const isLoginPage = window.location.pathname === '/auth0-callback';

    if (
      configAuthData?.platform === 'Auth0' &&
      !auth0IsLoading &&
      isLoginPage &&
      !authData.isAuthenticated &&
      !auth0IsAuthenticated &&
      !error &&
      !code
    ) {
      loginWithRedirect();
    }
  }, [authData.isAuthenticated, configAuthData?.platform, auth0IsLoading, auth0IsAuthenticated]);

  return <AuthContext.Provider value={{ ...authData, currentUser, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
